import type { JSX, MouseEvent, ReactNode } from 'react'
import type {
  CardContentTypeT,
  CardSizeT,
  CardTypeT,
} from 'blocks/Cards/Card.h'

import { useCallback, useMemo } from 'react'
import { ButtonIconSizes } from 'atoms/Button'
import { InlineCloseButton } from 'atoms/Button/Templates'
import { TextBoxMaxLine2Font2XlBold } from 'atoms/TextBox/Templates'
import { TextAligning } from 'atoms/TextBox/TextBox.h'
import { CardType } from 'blocks/Cards'
import {
  getRowCardAspectRatio,
  getRowCardAspectRatioVar,
} from 'containers/RowContainer/lib'
import {
  RowContainerVerticalGaps,
  RowControlsType,
} from 'containers/RowContainer/RowContainer.h'
import { RowSimpleContainer } from 'containers/RowContainer/RowSimpleContainer'
import { useClientResolution } from 'utils/hooks'
import { FIXED_INDENTS, INDENTS, ROW_GAP } from 'vars/index'
import {
  CloseHeaderSC,
  PlayerRecommendationsLayoutSC,
  RecommendationsContentSC,
  RecommendationsTitleSC,
} from './PlayerRecommendationsLayout.styled'
import { usePlayerRecommendationsVisibleCards } from './usePlayerRecommendationsVisibleCards'

export interface IPlayerRecommendationsLayoutProps {
  title: string
  children: ReactNode
  itemCount: number
  onClose: () => void
  contentType?: CardContentTypeT
  type?: CardTypeT
  size?: CardSizeT
}

const getRowHorizontalPaddings = (clientWidth: number): FIXED_INDENTS => {
  if (clientWidth < 600) return FIXED_INDENTS.INDENT_40

  if (clientWidth < 1240) return FIXED_INDENTS.INDENT_80

  if (clientWidth < 1640) return FIXED_INDENTS.INDENT_160

  return FIXED_INDENTS.INDENT_200
}

export const PlayerRecommendationsLayout = ({
  title,
  children,
  itemCount,
  onClose,
  type = CardType.HORIZONTAL,
}: IPlayerRecommendationsLayoutProps): JSX.Element => {
  const { width: clientWidth } = useClientResolution()

  const handleClose = useCallback(
    (event: MouseEvent) => {
      event.preventDefault()
      event.stopPropagation()
      onClose()
    },
    [onClose]
  )

  const stopPlayerEvent = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const visibleCards = usePlayerRecommendationsVisibleCards(clientWidth)

  const rowHorizontalPaddings = useMemo(
    () => getRowHorizontalPaddings(clientWidth),
    [clientWidth]
  )

  const rowCardAspectRatio = getRowCardAspectRatio(type)
  const rowCardAspectRatioVar = getRowCardAspectRatioVar(type)

  return (
    <PlayerRecommendationsLayoutSC
      onMouseDown={stopPlayerEvent}
      onMouseUp={stopPlayerEvent}
      onClick={stopPlayerEvent}
    >
      <CloseHeaderSC>
        <InlineCloseButton
          onClick={handleClose}
          paddings={[INDENTS.INDENT_0]}
          iconSize={ButtonIconSizes.xxMedium}
        />
      </CloseHeaderSC>
      <RecommendationsContentSC>
        <RecommendationsTitleSC>
          <TextBoxMaxLine2Font2XlBold
            title={title}
            alignment={TextAligning.Center}
            horizontalPaddings={FIXED_INDENTS.INDENT_0}
          />
        </RecommendationsTitleSC>
        <RowSimpleContainer
          itemCount={itemCount}
          visibleCards={visibleCards}
          gap={ROW_GAP.SIZE_30}
          controlsType={RowControlsType.inRowFluid}
          horizontalPaddings={rowHorizontalPaddings}
          verticalPaddingTop={FIXED_INDENTS.INDENT_0}
          verticalPaddingBottom={FIXED_INDENTS.INDENT_0}
          containerVerticalGap={RowContainerVerticalGaps.RowWithoutHeader}
          withSpecialCell={false}
          cardAspectRatio={rowCardAspectRatio}
          cardAspectRatioVar={rowCardAspectRatioVar}
        >
          {children}
        </RowSimpleContainer>
      </RecommendationsContentSC>
    </PlayerRecommendationsLayoutSC>
  )
}
